import React, { useState } from "react";
import { Card, Row, Col, Table } from "react-bootstrap";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ShieldExclamation,
  XCircleFill,
  CashStack,
  Percent,
  Activity,
  ExclamationTriangleFill,
  Building,
  ExclamationCircleFill,
} from "react-bootstrap-icons";

const DEFAULT_REJECTION_REASONS = [
  {
    id: 1,
    reason: "Missing Diagnosis",
    code: "NPH-101",
    category: "Clinical Justification",
    amount: 88200,
    count: 342,
  },
  {
    id: 2,
    reason: "Invalid Procedure Code",
    code: "NPH-204",
    category: "Coding & Billing",
    amount: 87100,
    count: 240,
  },
  {
    id: 3,
    reason: "Coverage Exceeded",
    code: "NPH-302",
    category: "Policy & Eligibility",
    amount: 64500,
    count: 185,
  },
  {
    id: 4,
    reason: "Expired Policy",
    code: "NPH-301",
    category: "Policy & Eligibility",
    amount: 52000,
    count: 150,
  },
  {
    id: 5,
    reason: "Duplicate Claim",
    code: "NPH-405",
    category: "Duplicate & Fraud",
    amount: 49000,
    count: 140,
  },
  {
    id: 6,
    reason: "Insufficient Clinical Justification",
    code: "NPH-105",
    category: "Clinical Justification",
    amount: 48800,
    count: 138,
  },
  {
    id: 7,
    reason: "Coding Error",
    code: "NPH-201",
    category: "Coding & Billing",
    amount: 43500,
    count: 125,
  },
  {
    id: 8,
    reason: "Authorization Missing",
    code: "NPH-501",
    category: "Prior Authorization",
    amount: 29600,
    count: 90,
  },
  {
    id: 9,
    reason: "Benefit Limit Reached",
    code: "NPH-308",
    category: "Policy & Eligibility",
    amount: 24500,
    count: 70,
  },
  {
    id: 10,
    reason: "Other",
    code: "NPH-999",
    category: "General Error",
    amount: 16800,
    count: 37,
  },
  {
    id: 11,
    reason: "Provider Network Restriction",
    code: "NPH-310",
    category: "Network & Provider",
    amount: 15400,
    count: 34,
  },
  {
    id: 12,
    reason: "Patient Share Unpaid",
    code: "NPH-315",
    category: "Billing & Copay",
    amount: 14200,
    count: 31,
  },
  {
    id: 13,
    reason: "Late Claim Submission",
    code: "NPH-401",
    category: "Policy & Eligibility",
    amount: 12500,
    count: 28,
  },
  {
    id: 14,
    reason: "Incorrect Modifier Used",
    code: "NPH-208",
    category: "Coding & Billing",
    amount: 11200,
    count: 25,
  },
  {
    id: 15,
    reason: "Member ID Mismatch",
    code: "NPH-110",
    category: "Patient Verification",
    amount: 9800,
    count: 22,
  },
  {
    id: 16,
    reason: "Invalid Service Date",
    code: "NPH-115",
    category: "General Error",
    amount: 8500,
    count: 19,
  },
  {
    id: 17,
    reason: "Prior Auth Expired",
    code: "NPH-505",
    category: "Prior Authorization",
    amount: 7200,
    count: 16,
  },
  {
    id: 18,
    reason: "Duplicate Preauth Number",
    code: "NPH-508",
    category: "Prior Authorization",
    amount: 6100,
    count: 14,
  },
  {
    id: 19,
    reason: "Unsupported Diagnosis Combo",
    code: "NPH-120",
    category: "Clinical Justification",
    amount: 5200,
    count: 11,
  },
  {
    id: 20,
    reason: "Missing Referring Physician",
    code: "NPH-125",
    category: "Clinical Justification",
    amount: 4400,
    count: 9,
  },
];

const METRIC_CARDS = [
  {
    title: "Rejected Claims",
    value: "1,898",
    icon: XCircleFill,
    iconColor: "#EF4444",
  },
  {
    title: "Rejected Amount",
    value: "SAR 501.4K",
    icon: CashStack,
    iconColor: "#EF4444",
  },
  {
    title: "Rejection %",
    value: "11.9%",
    icon: Percent,
    iconColor: "#EF4444",
  },
  {
    title: "Avg / Claim",
    value: "SAR 264",
    icon: Activity,
    iconColor: "#EF4444",
  },
  {
    title: "Top Reason",
    value: "Missing Diagnosis",
    icon: ExclamationTriangleFill,
    iconColor: "#EF4444",
  },
  {
    title: "Most Affected Payer",
    value: "MedGulf",
    icon: Building,
    iconColor: "#EF4444",
  },
];

const RejectionAnaltics = ({
  rejectionReasons = DEFAULT_REJECTION_REASONS,
}) => {
  const [viewMetric, setViewMetric] = useState("amount");
  const [hoveredIdx, setHoveredIdx] = useState(null);

  const totalRejectedAmount = 501400;

  const chartData = rejectionReasons.map((r) => ({
    name: r.reason.length > 25 ? r.reason.substring(0, 24) + "..." : r.reason,
    fullName: r.reason,
    code: r.code,
    category: r.category,
    val: viewMetric === "amount" ? r.amount : r.count,
    formattedVal:
      viewMetric === "amount"
        ? `SAR ${r.amount.toLocaleString()}`
        : `${r.count} claims`,
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          className="p-3 rounded shadow-lg text-white"
          style={{
            backgroundColor: "#1E293B",
            border: "1px solid #334155",
            fontSize: "12px",
            minWidth: "220px",
          }}
        >
          <div
            className="fw-bold pb-1 mb-2 border-bottom"
            style={{ color: "#F87171", borderColor: "#334155" }}
          >
            {data.fullName}
          </div>
          <div className="d-flex justify-content-between gap-3 mb-1">
            <span style={{ color: "#94A3B8" }}>NPHIES Error Code:</span>
            <span
              className="font-monospace fw-bold"
              style={{ color: "#FDE047" }}
            >
              {data.code}
            </span>
          </div>
          <div className="d-flex justify-content-between gap-3 mb-1">
            <span style={{ color: "#94A3B8" }}>Category:</span>
            <span className="fw-medium" style={{ color: "#E2E8F0" }}>
              {data.category}
            </span>
          </div>
          <div
            className="d-flex justify-content-between gap-3 pt-2 mt-1 border-top"
            style={{ borderColor: "#334155" }}
          >
            <span style={{ color: "#94A3B8" }}>Impact Value:</span>
            <span className="fw-bold" style={{ color: "#F87171" }}>
              {data.formattedVal}
            </span>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <Col xs={12} className="mt-3">
      <Card className="shadow-sm border">
        {/* Header with Shield Icon & Title */}
        <Card.Header className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between p-3 border-bottom gap-3">
          <div className="d-flex align-items-center gap-2">
            <div
              className="d-flex align-items-center justify-content-center flex-shrink-0"
              style={{
                width: "30px",
                height: "30px",
                borderRadius: "8px",
                backgroundColor: "rgba(239, 68, 68, 0.12)",
                color: "#EF4444",
                border: "1px solid rgba(239, 68, 68, 0.25)",
              }}
            >
              <ShieldExclamation size={15} />
            </div>
            <div>
              <h6
                className="mb-0 fw-bold"
                style={{ fontSize: "15px" }}
              >
                Rejection Analytics
              </h6>
              <div className="text-muted" style={{ fontSize: "12px" }}>
                Where claim value is being lost, and why
              </div>
            </div>
          </div>

          {/* Toggle Amount vs Count */}
          <div
            className="d-flex align-items-center gap-1 p-1 rounded"
            style={{ backgroundColor: "#F1F5F9" }}
          >
            <button
              type="button"
              onClick={() => setViewMetric("amount")}
              className="btn btn-sm border-0 fw-semi-bold px-3 py-1"
              style={{
                fontSize: "12px",
                backgroundColor:
                  viewMetric === "amount" ? "#EF4444" : "transparent",
                color: viewMetric === "amount" ? "#ffffff" : "#64748B",
                boxShadow:
                  viewMetric === "amount"
                    ? "0 1px 2px rgba(0, 0, 0, 0.1)"
                    : "none",
              }}
            >
              By Rejected SAR
            </button>
            <button
              type="button"
              onClick={() => setViewMetric("count")}
              className="btn btn-sm border-0 fw-semi-bold px-3 py-1"
              style={{
                fontSize: "12px",
                backgroundColor:
                  viewMetric === "count" ? "#EF4444" : "transparent",
                color: viewMetric === "count" ? "#ffffff" : "#64748B",
                boxShadow:
                  viewMetric === "count"
                    ? "0 1px 2px rgba(0, 0, 0, 0.1)"
                    : "none",
              }}
            >
              By Claim Count
            </button>
          </div>
        </Card.Header>

        <Card.Body className="p-4">
          {/* 6 Cards in ONE ROW on Desktop and Responsive */}
          <Row className="g-3 mb-4">
            {METRIC_CARDS.map((card, idx) => {
              const IconComp = card.icon;
              const isHovered = hoveredIdx === idx;
              return (
                <Col xs={12} sm={6} md={4} lg={2} key={card.title}>
                  <div
                    className="p-3 h-100 d-flex flex-column justify-content-between"
                    onMouseEnter={() => setHoveredIdx(idx)}
                    onMouseLeave={() => setHoveredIdx(null)}
                    style={{
                      backgroundColor: "rgba(239, 68, 68, 0.05)",
                      borderTop: isHovered
                        ? "1px solid rgba(239, 68, 68, 0.6)"
                        : "1px solid rgba(239, 68, 68, 0.2)",
                      borderRight: isHovered
                        ? "1px solid rgba(239, 68, 68, 0.6)"
                        : "1px solid rgba(239, 68, 68, 0.2)",
                      borderBottom: isHovered
                        ? "1px solid rgba(239, 68, 68, 0.6)"
                        : "1px solid rgba(239, 68, 68, 0.2)",
                      borderLeft: isHovered
                        ? "1px solid rgba(239, 68, 68, 0.6)"
                        : "1px solid rgba(239, 68, 68, 0.2)",
                      borderRadius: "12px",
                      boxShadow: isHovered
                        ? "0 8px 18px rgba(239, 68, 68, 0.12)"
                        : "0 1px 3px rgba(0, 0, 0, 0.02)",
                      transform: isHovered
                        ? "translateY(-3px)"
                        : "translateY(0)",
                      transition: "all 0.2s ease",
                      cursor: "pointer",
                    }}
                  >
                    <div>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div
                          className="d-flex align-items-center justify-content-center rounded-circle"
                          style={{
                            width: "26px",
                            height: "26px",
                            backgroundColor: "rgba(239, 68, 68, 0.1)",
                            color: "#EF4444",
                          }}
                        >
                          <IconComp size={13} />
                        </div>
                        <span
                          className="fw-bold"
                          style={{ fontSize: "10px", color: "#EF4444" }}
                        >
                          CRITICAL
                        </span>
                      </div>
                      <div
                        className="fw-semi-bold text-muted"
                        style={{ fontSize: "11px" }}
                      >
                        {card.title}
                      </div>
                    </div>
                    <div
                      className="fw-bold mt-1 text-truncate"
                      style={{
                        fontSize: card.value.length > 10 ? "13px" : "16px",
                      }}
                      title={card.value}
                    >
                      {card.value}
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>

          {/* Top 10 Rejection Reasons Bar Chart Section */}
          <div className="mt-4 pt-2">
            <div
              className="fw-bold text-uppercase mb-3"
              style={{
                fontSize: "11px",
                color: "#64748B",
                letterSpacing: "0.5px",
              }}
            >
              TOP 10 REJECTION REASONS
            </div>

            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  layout="vertical"
                  data={chartData.slice(0, 10)}
                  margin={{ top: 4, right: 40, left: 8, bottom: 4 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    horizontal={false}
                    stroke="rgba(148, 163, 184, 0.15)"
                  />
                  <XAxis
                    type="number"
                    tick={{ fill: "#64748B", fontSize: 11 }}
                    tickFormatter={(val) =>
                      viewMetric === "amount"
                        ? `SAR ${(val / 1000).toFixed(0)}K`
                        : val
                    }
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fill: "#334155", fontSize: 11, fontWeight: 500 }}
                    width={190}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "rgba(239, 68, 68, 0.08)" }}
                    wrapperStyle={{ zIndex: 1000 }}
                  />
                  <Bar
                    dataKey="val"
                    fill="#EF4444"
                    radius={[0, 6, 6, 0]}
                    barSize={20}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Card.Body>
      </Card>
    </Col>
  );
};

export default RejectionAnaltics;
